<template>
  <div class="errors-log-list-wrapper">
    <LogSearch v-model="errorsSearch" />

    <div ref="containerRef" class="errors-log-list" data-testid="logs-errors-list">
      <div
        v-for="entry in entries"
        :key="entry.id"
        class="error-entry"
        :class="{ 'error-entry--active': entry.id === activeEntryId }"
        :data-log-id="entry.id"
        :data-testid="`error-entry-${entry.id}`"
      >
        <button type="button" class="error-entry__header" @click="$emit('seek', entry.timestamp)">
          <span class="error-entry__level">error</span>
          <span class="error-entry__timestamp">{{ formatLogTimestamp(entry.timestamp) }}</span>
          <span class="error-entry__message">{{ entry.message }}</span>
        </button>
        <pre v-if="entry.stack" class="error-entry__stack">{{ entry.stack }}</pre>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import LogSearch from './LogSearch.vue';
import { storeToRefs } from 'pinia';
import { formatLogTimestamp } from '@/modules/viewer/composables/useLogs';
import { useLogsStore } from '@/modules/viewer/store/useLogsStore';
import { useActiveLogEntry } from '@/modules/viewer/composables/useActiveLogEntry';

const props = defineProps<{
  autoScroll: boolean;
}>();

defineEmits<{
  seek: [timestamp: number];
}>();

const { filteredErrorEntries, errorsSearch } = storeToRefs(useLogsStore());
const entries = computed(() => filteredErrorEntries.value);

const { containerRef, activeEntryId } = useActiveLogEntry(entries, toRef(props, 'autoScroll'));
</script>

<style scoped>
.errors-log-list-wrapper {
  @apply flex flex-col;
}

.errors-log-list {
  @apply flex flex-col;
}

.error-entry {
  @apply border-b border-zinc-100 dark:border-zinc-700/60 py-2 px-4 transition-colors;
}

.error-entry--active {
  @apply bg-blue-50 dark:bg-blue-900/20;
}

.error-entry__header {
  @apply flex items-start gap-3 w-full text-left cursor-pointer;
}

.error-entry__level {
  @apply flex-shrink-0 inline-flex items-center justify-center min-w-[48px] px-2 py-0.5 text-xs font-semibold uppercase rounded;
  @apply bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400;
}

.error-entry__timestamp {
  @apply flex-shrink-0 font-mono text-xs text-blue-600 dark:text-blue-400 pt-0.5;
}

.error-entry__message {
  @apply flex-grow text-sm text-zinc-800 dark:text-zinc-200 break-words min-w-0;
}

.error-entry__stack {
  @apply mt-2 ml-14 text-xs font-mono text-zinc-600 dark:text-zinc-400 whitespace-pre-wrap break-words;
}
</style>
